import { lazy } from 'react';
import PoolIcon from '@mui/icons-material/Pool';
import * as exerciseDefinitionApi from '../../services/exerciseDefinitionApi';
import { getModalityIcon } from '../../config/modalityConfig';

const ActivityForm = lazy(() => import('./ActivityForm'));
const ActivityView = lazy(() => import('./ActivityView'));

const ENABLE_PERSONALIZED_ACTIVITIES = true;

export const activityConfig = {
  id: 'activities',
  resourceType: 'exercise_definition',
  singularName: 'Activity',
  pluralName: 'Exercises & Activities',
  icon: (activity) => {
    const modality = activity?.taxonomy?.modalities?.[0];
    return getModalityIcon(modality) || PoolIcon;
  },
  searchable: false,
  sortable: false,
  enableFavorites: true,
  favoriteResourceType: 'exercise_definition',
  usePersonalizedList: ENABLE_PERSONALIZED_ACTIVITIES,
  defaultToFavorites: true,
  modalSize: 'md',
  searchFilter: (item, query, additionalProps) => {
    const q = query.toLowerCase();
    const nameMatch = item.name?.toLowerCase().includes(q);
    const descriptionMatch = item.description?.toLowerCase().includes(q);
    const modalityMatch = (item.taxonomy?.modalities || []).some(m => m.toLowerCase().includes(q));
    const primaryMusclesMatch = (item.primary_muscle_groups || []).some(m => m.toLowerCase().includes(q));
    const secondaryMusclesMatch = (item.secondary_muscle_groups || []).some(m => m.toLowerCase().includes(q));
    const aliasesMatch = (item.aliases || []).some(a => a.toLowerCase().includes(q));
    return nameMatch || descriptionMatch || modalityMatch || primaryMusclesMatch || secondaryMusclesMatch || aliasesMatch;
  },
  getDisplayName: (item) => item.name || '',
  sortOptions: [
    {
      label: 'Sort by Name',
      value: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      label: 'Sort by Modality',
      value: 'modality',
      sorter: (a, b, additionalProps) => {
        const modalityA = a.taxonomy?.modalities?.[0] || 'ZZZ_Uncategorized';
        const modalityB = b.taxonomy?.modalities?.[0] || 'ZZZ_Uncategorized';
        if (modalityA === modalityB) {
          return (a.name || '').localeCompare(b.name || '');
        }
        return modalityA.localeCompare(modalityB);
      },
    },
    {
      label: 'Sort by Popularity',
      value: 'popularity',
      sorter: (a, b) => (b.popularity || 0) - (a.popularity || 0),
    }
  ],
  getInitialState: (data) => ({
    id: data?.id || null,
    name: data?.name || '',
    description: data?.description || '',
    aliases: data?.aliases || [],
    primary_muscle_groups: data?.primary_muscle_groups || [],
    secondary_muscle_groups: data?.secondary_muscle_groups || [],
    expected_measurements: data?.expected_measurements || [],
    taxonomy: {
      modalities: data?.taxonomy?.modalities || [],
      pattern: data?.taxonomy?.pattern || '',
      mechanics: data?.taxonomy?.mechanics || '',
      force_type: data?.taxonomy?.force_type || '',
      plane_of_motion: data?.taxonomy?.plane_of_motion || '',
      popularity: data?.popularity || 3,
    },
    setup: {
      posture: data?.setup?.posture || '',
      laterality: data?.setup?.laterality || '',
      elevation: data?.setup?.elevation || '',
      load_position: data?.setup?.load_position || '',
      stance: data?.setup?.stance || '',
      grip: data?.setup?.grip || '',
    },
    equipment: {
      is_bodyweight: data?.equipment?.is_bodyweight ?? false,
      equipment_ids: data?.equipment?.equipment_ids || [],
    },
    details: data?.details || null,
    as_system: data?.user_id === 'system_user',
    skip_dedupe: false,
    user_id: data?.user_id,
    shadows_id: data?.shadows_id || null,
    _isPartial: data?._isPartial || false,
  }),
  api: {
    getAll: () => exerciseDefinitionApi.searchExerciseDefinitions({
      scope: ENABLE_PERSONALIZED_ACTIVITIES ? 'personalized' : undefined,
      fields: 'id,name,taxonomy,primary_muscle_groups,secondary_muscle_groups,aliases,description,user_id,popularity'
    }),
    create: exerciseDefinitionApi.createExerciseDefinition,
    update: exerciseDefinitionApi.updateExerciseDefinition,
    delete: exerciseDefinitionApi.deleteExerciseDefinition,
    getById: exerciseDefinitionApi.getExerciseDefinitionById,
  },
  FormComponent: ActivityForm,
  ViewComponent: ActivityView,
};